import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cartAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';

const Navigation = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch cart count
  useEffect(() => {
    const fetchCartCount = async () => {
      try {
        const response = await cartAPI.get();
        const itemCount = response.data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
        setCartCount(itemCount);
      } catch (error) {
        console.error('Failed to fetch cart:', error);
      }
    };
    fetchCartCount();
    
    // Refresh cart count every 3 seconds when on page
    const interval = setInterval(fetchCartCount, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'glass border-b border-white/10' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="text-2xl font-outfit font-semibold text-white tracking-tight hover:scale-105 transition-transform">
            Proxy <span className="text-violet-600">Magic</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/shop" className="text-zinc-400 hover:text-white transition-colors">
              {t('nav.shop')}
            </Link>
            <Link to="/card-builder" className="text-zinc-400 hover:text-white transition-colors">
              {t('nav.deckBuilder') || 'Deck Builder'}
            </Link>
            <Link to="/terms" className="text-zinc-400 hover:text-white transition-colors">
              TOS
            </Link>
            <Link to="/contact" className="text-zinc-400 hover:text-white transition-colors">
              {t('nav.contact') || 'Contact'}
            </Link>
            {isAuthenticated && (
              <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors">
                {t('nav.orders')}
              </Link>
            )}
          </div>

          {/* Right Side Icons */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />
            
            <Link to="/cart" className="relative p-2 text-zinc-400 hover:text-white transition-colors">
              <ShoppingCart size={24} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-violet-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {cartCount}
                </span>
              )}
            </Link>
            
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <Link to="/dashboard" className="flex items-center space-x-2 p-2 text-zinc-400 hover:text-white transition-colors">
                  <User size={24} />
                  <span className="text-sm">{user?.name}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  {t('nav.logout')}
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="px-6 py-2 bg-violet-600 text-white rounded-md font-medium hover:bg-violet-500 transition-all glow-violet"
              >
                {t('nav.login')}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-white"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="flex flex-col space-y-4">
              <Link to="/shop" className="text-zinc-400 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.shop')}
              </Link>
              <Link to="/card-builder" className="text-zinc-400 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.deckBuilder') || 'Deck Builder'}
              </Link>
              <Link to="/terms" className="text-zinc-400 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                TOS
              </Link>
              <Link to="/contact" className="text-zinc-400 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.contact') || 'Contact'}
              </Link>
              {isAuthenticated && (
                <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav.orders')}
                </Link>
              )}
              <Link to="/cart" className="text-zinc-400 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                Cart ({cartCount})
              </Link>
              <div className="pt-2">
                <LanguageSwitcher />
              </div>
              {isAuthenticated ? (
                <>
                  <div className="text-sm text-zinc-400">Hi, {user?.name}</div>
                  <button
                    onClick={handleLogout}
                    className="text-left text-zinc-400 hover:text-white transition-colors"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <Link to="/login" className="text-violet-600 hover:text-violet-500 transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav.login')}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
